'use client'

import { useCallback, useMemo, useState } from 'react'
import { type AnalyticsMappingContext } from '@shipt/cdui-core/modules/analytics'
import { type ActionResult, type OnAction } from '@shipt/cdui-modules/action-provider'
import {
  type CouponActionPayload,
  type CouponTermsActionPayload,
  couponActionPayloadSchema,
  couponTermsActionPayloadSchema,
} from '@shipt/cdui-modules/coupon-shelf/schema'
import {
  type ProductActionPayload,
  productActionPayloadSchema,
} from '@shipt/cdui-modules/product-shelf/schema'
import { routes, getRetailerCouponUrl } from '@shared/constants/routes'
import { useIsGuestUser, useUserStoreName } from '@/features/account/services/User/hooks'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { isSafeSegwayURL } from '@/features/shared/utils/url'
import { logWarn } from '@/features/shared/utils/logger'
import { useClipCouponAndCalculateSavings } from '@/features/shop/services/Coupons/hooks'
import {
  getCouponPromoCardProperties,
  trackPromoCardClicked,
  trackPromoCardViewed,
} from '@/features/shared/analytics/promoCard'
import { useCduiProductCommerce } from '@/features/cdui/useCduiProductCommerce'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicCouponTermsModal } from '@/features/shop/components/Modal/CouponTermsModal/DynamicCouponTermsModal'
import { useShowToast } from '@/features/shared/state/Toast/hooks'
import { ToastType } from '@/features/shared/constants/toastTypes'

export const CLIP_COUPON_ERROR = 'Error clipping coupon. Please try again.'

type ProductActionEffect = (
  payload: ProductActionPayload,
  analyticsContext?: AnalyticsMappingContext,
) => void

type CouponActionEffect = (
  coupon: CouponActionPayload,
  analyticsContext?: AnalyticsMappingContext,
) => void

/**
 * What a clip reports back to the card. A promise shows the coupon
 * clipped at once and rolls it back if it settles on anything but success, a
 * synchronous non success keeps it unclipped which means nothing leaves the card as it was.
 */
type ClipCouponOutcome = ActionResult | void | Promise<ActionResult | void>

type ClipCouponEffect = (
  coupon: CouponActionPayload,
  analyticsContext?: AnalyticsMappingContext,
) => ClipCouponOutcome

type CouponTermsActionEffect = (
  coupon: CouponTermsActionPayload,
  analyticsContext?: AnalyticsMappingContext,
) => void

type CduiActionEffects = {
  addProduct: ProductActionEffect
  removeProduct: ProductActionEffect
  viewProduct: ProductActionEffect
  clickProduct: ProductActionEffect
  selectBackup: ProductActionEffect
  clipCoupon: ClipCouponEffect
  shopCoupon: CouponActionEffect
  viewCouponTerms: CouponTermsActionEffect
  viewCoupon: CouponActionEffect
  handleBannerEvent: (event: Record<string, unknown>) => void
}

const PRODUCT_ACTIONS = {
  'product-shelf:add_to_cart': 'addProduct',
  'product-shelf:remove_from_cart': 'removeProduct',
  'product-shelf:view_product': 'viewProduct',
  'product-shelf:click_product': 'clickProduct',
  'product-shelf:select_backup': 'selectBackup',
} as const

type ProductActionType = keyof typeof PRODUCT_ACTIONS

const isProductAction = (type: string): type is ProductActionType =>
  Object.hasOwn(PRODUCT_ACTIONS, type)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : undefined

const stringField = (
  value: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  const field = value?.[key]
  return typeof field === 'string' && field.length > 0 ? field : undefined
}

const productPayload = (payload: unknown): ProductActionPayload | undefined => {
  const parsed = productActionPayloadSchema.safeParse(payload)
  return parsed.success ? parsed.data : undefined
}

const couponPayload = (payload: unknown): CouponActionPayload | undefined => {
  const parsed = couponActionPayloadSchema.safeParse(payload)
  return parsed.success ? parsed.data : undefined
}

const couponTermsPayload = (payload: unknown): CouponTermsActionPayload | undefined => {
  const parsed = couponTermsActionPayloadSchema.safeParse(payload)
  return parsed.success ? parsed.data : undefined
}

export const createCduiActionHandler = (effects: CduiActionEffects): OnAction => {
  return (action) => {
    if (isProductAction(action.type)) {
      const payload = productPayload(action.payload)
      if (payload) effects[PRODUCT_ACTIONS[action.type]](payload, action.analyticsContext)
      return
    }

    const data = asRecord(action.payload)

    switch (action.type) {
      case 'product-shelf:clip_coupon':
      case 'coupon-shelf:clip_coupon': {
        const payload = couponPayload(action.payload)
        // hand the outcome back so the card can roll back a clip that did not happen
        return payload ? effects.clipCoupon(payload, action.analyticsContext) : undefined
      }
      case 'coupon-shelf:shop_coupon': {
        const payload = couponPayload(action.payload)
        if (payload) effects.shopCoupon(payload, action.analyticsContext)
        return
      }
      case 'coupon-shelf:view_terms': {
        const payload = couponTermsPayload(action.payload)
        if (payload) effects.viewCouponTerms(payload, action.analyticsContext)
        return
      }
      case 'coupon-shelf:view_coupon': {
        const payload = couponPayload(action.payload)
        if (payload) effects.viewCoupon(payload, action.analyticsContext)
        return
      }
      case 'promo-banner:press_action':
      case 'promo-asset-banner:press_action': {
        const event = asRecord(data?.event)
        if (event) effects.handleBannerEvent(event)
        return
      }
      default:
        return
    }
  }
}

export const useCduiActions = (): OnAction => {
  const { addProduct, removeProduct, viewProduct, clickProduct, selectBackup } =
    useCduiProductCommerce()
  const router = useSegwayRouter()
  const currentPath = usePathWithSearch()
  const isGuestUser = useIsGuestUser()
  const storeName = useUserStoreName()
  const { clipCouponAndCalculateSavings } = useClipCouponAndCalculateSavings()
  const { openDialog } = useHandleDialog()
  const { showToast } = useShowToast()

  // CDUI's clip button can't disable itself while the clip is in flight (same as the
  // product cards), so a double tap would fire two clip mutations and two clip events.
  // a stable Set per mount rather than a ref, since the handler is built in a useMemo.
  const [couponsInFlight] = useState(() => new Set<string>())

  const handleClipCoupon = useCallback<ClipCouponEffect>(
    ({ couponId, couponIndex }) => {
      // answered synchronously so the card never shows a guest's coupon as clipped
      if (isGuestUser) {
        void router.push(`${routes.SIGN_UP.url}?next=${encodeURIComponent(currentPath)}`)
        return { status: 'cancelled' }
      }
      // the first tap's promise already owns the card's clipped state, so the ignored
      // tap reports nothing rather than a failure that would roll it back early
      if (couponsInFlight.has(couponId)) return
      couponsInFlight.add(couponId)

      return (async (): Promise<ActionResult> => {
        try {
          await clipCouponAndCalculateSavings({ couponId }, { listIndex: couponIndex })
          return { status: 'success' }
        } catch {
          // segway's own coupon surfaces show a clip failure inline, CDUI's card has no
          // error state, so surface it here instead of failing silently. The error
          // result rolls the card's optimistic clip back.
          showToast(ToastType.ICON_TOAST, {
            ariaLabel: CLIP_COUPON_ERROR,
            props: { title: CLIP_COUPON_ERROR, iconType: 'alert' },
          })
          return { status: 'error', message: CLIP_COUPON_ERROR }
        } finally {
          couponsInFlight.delete(couponId)
        }
      })()
    },
    [clipCouponAndCalculateSavings, couponsInFlight, currentPath, isGuestUser, router, showToast],
  )

  const handleShopCoupon = useCallback<CouponActionEffect>(
    ({ couponId, couponIndex }, analyticsContext) => {
      trackPromoCardClicked(
        getCouponPromoCardProperties({
          coupon: { couponId },
          listIndex: couponIndex,
          shelfIndex: analyticsContext?.shelfIndex,
          actionType: 'shop',
        }),
        // the coupon page is a pages router page so this is a hard nav, send it now or we lose it
        { sendImmediately: true },
      )
      void router.push(getRetailerCouponUrl(storeName, couponId))
    },
    [router, storeName],
  )

  const handleViewCouponTerms = useCallback<CouponTermsActionEffect>(
    ({ couponId, couponIndex, disclaimer, expiredAt }, analyticsContext) => {
      trackPromoCardClicked(
        getCouponPromoCardProperties({
          coupon: { couponId },
          listIndex: couponIndex,
          shelfIndex: analyticsContext?.shelfIndex,
          actionType: 'coupon_terms',
        }),
      )
      openDialog(DynamicCouponTermsModal, { coupon: { disclaimer, expiredAt } })
    },
    [openDialog],
  )

  const handleViewCoupon = useCallback<CouponActionEffect>(
    ({ couponId, couponIndex }, analyticsContext) => {
      trackPromoCardViewed(
        getCouponPromoCardProperties({
          coupon: { couponId },
          listIndex: couponIndex,
          shelfIndex: analyticsContext?.shelfIndex,
        }),
      )
    },
    [],
  )

  const handleBannerEvent = useCallback(
    (event: Record<string, unknown>) => {
      const kind = stringField(event, 'kind')
      if (kind === 'start_signup') {
        void router.push(routes.SIGN_UP.url)
        return
      }
      if (kind === 'open_deep_link') {
        const link = asRecord(event.link)
        const href = stringField(link, 'href')
        if (!href) return
        if (!isSafeSegwayURL(href)) {
          logWarn('[CDUI] Blocked unsafe deep link', { href })
          return
        }
        if (link?.newWindow === true) {
          const anchor = document.createElement('a')
          anchor.href = href
          anchor.target = '_blank'
          anchor.rel = link.noReferrer === true ? 'noopener noreferrer' : 'noopener'
          anchor.click()
        } else {
          void router.push(href)
        }
        return
      }
      if (kind === 'open_sheet' || kind === 'apply_promo_code') {
        logWarn('[CDUI] Unsupported banner action', { kind })
      }
    },
    [router],
  )

  return useMemo(
    () =>
      createCduiActionHandler({
        addProduct,
        removeProduct,
        viewProduct,
        clickProduct,
        selectBackup,
        clipCoupon: handleClipCoupon,
        shopCoupon: handleShopCoupon,
        viewCouponTerms: handleViewCouponTerms,
        viewCoupon: handleViewCoupon,
        handleBannerEvent,
      }),
    [
      addProduct,
      clickProduct,
      handleBannerEvent,
      handleClipCoupon,
      handleShopCoupon,
      handleViewCoupon,
      handleViewCouponTerms,
      removeProduct,
      selectBackup,
      viewProduct,
    ],
  )
}
